import { useCallback, useMemo } from "react"
import { useDispatch } from "react-redux"
import { Creators as ApiWrapperCreators } from "model/apiWrapper"
import useResource from "hooks/resources"
import { useAccountAlias } from "resources/account"
import { ResourceResult, DataStatus, OK, ERROR, LOADING } from "helpers/data-statuses"
import { Uuid } from "helpers/types/uuid"
import type { WorkspaceStateV1 } from "connected-components/dashboard/contracts/layouts"

// A single row from GET /api/:account/dashboard/instances. The server owns these
// shapes; the client trusts them (see contracts/layouts ADR 0003).
export interface DashboardInstance {
  instanceId: Uuid
  // Human-readable URL alias (server-generated, stable); the route resolver maps it back to instanceId.
  slug: string
  name: string
  layoutId: Uuid
  layoutVersionSeeded: number
  lastOpenedAt: string | null
  // Present on GET-by-id (the instance's saved autosave snapshot); absent on the list endpoint
  // and on a freshly created instance that has never autosaved.
  workspace?: WorkspaceStateV1
}

// Wire shape from the REST endpoints. Server keys by `id`; the client domain calls it `instanceId`,
// so the read hooks rename it here. ADR 0003 trust: rename the identifier, don't re-validate the row.
interface DashboardInstanceWire {
  id: Uuid
  slug: string
  name: string
  layoutId: Uuid
  layoutVersionSeeded: number
  lastOpenedAt: string | null
  workspace?: WorkspaceStateV1
}

function toDashboardInstance({ id, ...rest }: DashboardInstanceWire): DashboardInstance {
  return { instanceId: id, ...rest }
}

// Rebuilds the discriminated ResourceResult per status (data is non-null only on OK, so a loose
// [data, status] tuple won't type). Pass memoized data to keep refs stable.
function asResourceResult<R>(data: R | null, status: DataStatus): ResourceResult<R> {
  switch (status) {
    case OK: return [data, OK]
    case ERROR: return [null, ERROR]
    case LOADING: return [null, LOADING]
    default: return [null, status]
  }
}

// Body for POST /dashboard/instances. The id is a client-generated UUID. Workspace is NOT sent at
// create (create only links the layout); the snapshot is persisted by a follow-up workspace PUT.
export interface CreateInstancePayload {
  instanceId: Uuid
  name: string
  layoutId: Uuid
}

// The `state.global.data` resource key for the instances list. Exported so callers that
// mutate the list server-side (e.g. lazy migration) can reset the cache to force a refetch.
export const DASHBOARD_INSTANCES_RESOURCE = "dashboardInstances"

// --- Reads: list fetch ---
// `enabled` defaults to true; pass false (e.g. multi-dashboards flag off) to skip the
// fetch entirely — the null url short-circuits useResource to [null, OK].
export function useDashboardInstances(enabled = true): ResourceResult<DashboardInstance[]> {
  const account = useAccountAlias()

  const [rows, status] = useResource<DashboardInstanceWire[]>({
    resource: DASHBOARD_INSTANCES_RESOURCE,
    url: () => enabled && account ? `/api/${account}/dashboard/instances` : null,
  }, DASHBOARD_INSTANCES_RESOURCE)

  // Memoize the id→instanceId map: a fresh array every render would churn the route resolver and instances slice.
  const instances = useMemo(() => (rows ? rows.map(toDashboardInstance) : null), [rows])
  return asResourceResult(instances, status)
}

// --- Reads: single-item fetch (one instance by id, not the list above) ---
export function useDashboardInstance(instanceId: string | null): ResourceResult<DashboardInstance> {
  const account = useAccountAlias()

  const [row, status] = useResource<DashboardInstanceWire>({
    resource: "dashboardInstance",
    url: () => account && instanceId ? `/api/${account}/dashboard/instances/${instanceId}` : null,
  }, "dashboardInstance")

  const instance = useMemo(() => (row ? toDashboardInstance(row) : null), [row])
  return asResourceResult(instance, status)
}

// --- Writes ---
export function useCreateInstance() {
  const account = useAccountAlias()
  const dispatch = useDispatch()

  return useCallback(
    ({ instanceId, name, layoutId }: CreateInstancePayload): Promise<DashboardInstance> =>
      new Promise<DashboardInstance>((resolve, reject) => {
        if (!account) return reject(new Error("No account selected"))
        // Server create schema is { id, layoutId, name }, additionalProperties:false — send the uuid as `id`, nothing else.
        dispatch(ApiWrapperCreators.post(
          `/api/${account}/dashboard/instances`,
          { id: instanceId, layoutId, name },
          (row: DashboardInstanceWire) => resolve(toDashboardInstance(row)),
          reject,
        ))
      }),
    [account, dispatch],
  )
}

// Uncurried workspace PUT for callers that mint the instance id at call time (the lazy migration).
// useUpdateInstanceWorkspace is the curried variant the autosave writer binds to a fixed instance.
export function useSaveInstanceWorkspace() {
  const account = useAccountAlias()
  const dispatch = useDispatch()

  return useCallback(
    (instanceId: string, workspace: WorkspaceStateV1): Promise<void> =>
      new Promise<void>((resolve, reject) => {
        if (!account) return reject(new Error("No account selected"))
        dispatch(ApiWrapperCreators.put(`/api/${account}/dashboard/instances/${instanceId}/workspace`, workspace, resolve, reject))
      }),
    [account, dispatch],
  )
}

export function useUpdateInstanceWorkspace(instanceId: string | null) {
  const account = useAccountAlias()
  const dispatch = useDispatch()

  return useCallback(
    (workspace: WorkspaceStateV1): Promise<void> =>
      new Promise<void>((resolve, reject) => {
        if (!account || !instanceId) return reject(new Error("Missing account or instance id"))
        dispatch(ApiWrapperCreators.put(`/api/${account}/dashboard/instances/${instanceId}/workspace`, workspace, resolve, reject))
      }),
    [account, instanceId, dispatch],
  )
}

// Page-teardown variant of useUpdateInstanceWorkspace: dispatches putKeepalive so the
// final autosave survives the document being unloaded (the visibilitychange→hidden flush).
export function useUpdateInstanceWorkspaceKeepalive(instanceId: string | null) {
  const account = useAccountAlias()
  const dispatch = useDispatch()

  return useCallback(
    (workspace: WorkspaceStateV1): Promise<void> =>
      new Promise<void>((resolve, reject) => {
        if (!account || !instanceId) return reject(new Error("Missing account or instance id"))
        dispatch(ApiWrapperCreators.putKeepalive(`/api/${account}/dashboard/instances/${instanceId}/workspace`, workspace, resolve, reject))
      }),
    [account, instanceId, dispatch],
  )
}

export function useTouchInstance() {
  const account = useAccountAlias()
  const dispatch = useDispatch()

  return useCallback(
    (instanceId: string): Promise<void> =>
      new Promise<void>((resolve, reject) => {
        if (!account) return reject(new Error("No account selected"))
        // The PATCH schema bumps recency via { touch: true }; the server stamps lastOpenedAt itself.
        dispatch(ApiWrapperCreators.patch(`/api/${account}/dashboard/instances/${instanceId}`, { touch: true }, resolve, reject))
      }),
    [account, dispatch],
  )
}

export function useRenameInstance() {
  const account = useAccountAlias()
  const dispatch = useDispatch()

  return useCallback(
    (instanceId: string, name: string): Promise<void> =>
      new Promise<void>((resolve, reject) => {
        if (!account) return reject(new Error("No account selected"))
        dispatch(ApiWrapperCreators.patch(`/api/${account}/dashboard/instances/${instanceId}`, { name }, resolve, reject))
      }),
    [account, dispatch],
  )
}

export function useDeleteInstance() {
  const account = useAccountAlias()
  const dispatch = useDispatch()

  return useCallback(
    (instanceId: string): Promise<void> =>
      new Promise<void>((resolve, reject) => {
        if (!account) return reject(new Error("No account selected"))
        dispatch(ApiWrapperCreators.delete(`/api/${account}/dashboard/instances/${instanceId}`, undefined, resolve, reject))
      }),
    [account, dispatch],
  )
}
